import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ReferenceArea } from 'recharts';

type WeightEntry = {
  _id?: string;
  date: string; // ISO
  weightKg: number;
};

type Dose = {
  _id?: string;
  date: string; // ISO
  doseAmount: number; // mg
};

type Symptom = {
  _id?: string;
  date: string; // ISO
  category: string;
  severity: number; // 0-10
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function formatDateLocal(dateIso: string) {
  const d = new Date(dateIso);
  return d.toLocaleDateString('en-GB');
}

function round1(n: number) {
  return Math.round(n * 10) / 10;
}

const JourneyDashboardPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [weights, setWeights] = useState<WeightEntry[]>([]);
  const [doses, setDoses] = useState<Dose[]>([]);
  const [symptoms, setSymptoms] = useState<Symptom[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Chart display preferences and height
  const [showDoseMarkers, setShowDoseMarkers] = useState<boolean>(() => {
    const v = localStorage.getItem('dashShowDoseMarkers');
    return v === null ? true : v === 'true';
  });
  const [showBmiBands, setShowBmiBands] = useState<boolean>(() => {
    const v = localStorage.getItem('dashShowBmiBands');
    return v === 'true';
  });
  const [heightCm, setHeightCm] = useState<string>('');

  // Goals (persist locally for now)
  const [goalWeightKg, setGoalWeightKg] = useState<string>(() => localStorage.getItem('dashboardGoalWeightKg') || '');
  const [goalDate, setGoalDate] = useState<string>(() => localStorage.getItem('dashboardGoalDate') || '');

  useEffect(() => {
    localStorage.setItem('dashboardGoalWeightKg', goalWeightKg);
  }, [goalWeightKg]);

  useEffect(() => {
    localStorage.setItem('dashboardGoalDate', goalDate);
  }, [goalDate]);

  useEffect(() => {
    localStorage.setItem('dashShowDoseMarkers', String(showDoseMarkers));
  }, [showDoseMarkers]);
  useEffect(() => {
    localStorage.setItem('dashShowBmiBands', String(showBmiBands));
  }, [showBmiBands]);
  useEffect(() => {
    // Initialize height from user profile or localStorage
    if (user && typeof user.heightCm === 'number' && user.heightCm > 0) {
      setHeightCm(String(user.heightCm));
      localStorage.setItem('userHeightCm', String(user.heightCm));
    } else {
      const v = localStorage.getItem('userHeightCm') || '';
      setHeightCm(v);
    }
  }, [user]);

  const saveHeight = useCallback(async (cm: number) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/user/height`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ heightCm: cm })
      });
      if (!res.ok) throw new Error('Failed to save height');
    } catch (e: any) {
      setError(e?.message || 'Failed to save height');
    }
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      if (!isAuthenticated) return;
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` } as any;
        const [wRes, dRes, sRes] = await Promise.all([
          fetch(`${API_BASE_URL}/weights`, { headers }),
          fetch(`${API_BASE_URL}/doses`, { headers }),
          fetch(`${API_BASE_URL}/symptoms`, { headers }),
        ]);
        if (!wRes.ok) throw new Error('Failed to load weights');
        if (!dRes.ok) throw new Error('Failed to load doses');
        if (!sRes.ok) throw new Error('Failed to load symptoms');
        const [wData, dData, sData] = await Promise.all([wRes.json(), dRes.json(), sRes.json()]);
        setWeights(Array.isArray(wData) ? wData : []);
        setDoses(Array.isArray(dData) ? dData : []);
        setSymptoms(Array.isArray(sData) ? sData : []);
      } catch (e: any) {
        setError(e?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [isAuthenticated]);

  // Sort weights ascending by date
  const sortedWeights = useMemo(() => {
    return [...weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [weights]);

  // Base series (actual weights with timestamps)
  const baseSeries = useMemo(() => sortedWeights.map(w => ({ timestamp: new Date(w.date).getTime(), date: w.date, weight: w.weightKg })), [sortedWeights]);

  // Linear regression over last ~8 weeks (fallback to all if too few points)
  const regression = useMemo(() => {
    if (!baseSeries.length) return { slopeKgPerWeek: undefined as number | undefined, intercept: undefined as number | undefined, t0Weeks: undefined as number | undefined };
    const lastWindowMs = 56 * 24 * 60 * 60 * 1000;
    const lastTs = baseSeries[baseSeries.length - 1].timestamp;
    const windowStart = lastTs - lastWindowMs;
    const windowPoints = baseSeries.filter(p => p.timestamp >= windowStart);
    const pts = (windowPoints.length >= 3 ? windowPoints : baseSeries);
    if (pts.length < 2) return { slopeKgPerWeek: undefined, intercept: undefined, t0Weeks: undefined };
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const t0Weeks = pts[0].timestamp / weekMs;
    const xs = pts.map(p => p.timestamp / weekMs - t0Weeks);
    const ys = pts.map(p => p.weight);
    const n = xs.length;
    const sumX = xs.reduce((a, b) => a + b, 0);
    const sumY = ys.reduce((a, b) => a + b, 0);
    const sumXX = xs.reduce((a, b) => a + b * b, 0);
    const sumXY = xs.reduce((acc, x, i) => acc + x * ys[i], 0);
    const denom = n * sumXX - sumX * sumX;
    if (denom === 0) return { slopeKgPerWeek: undefined, intercept: undefined, t0Weeks };
    const slope = (n * sumXY - sumX * sumY) / denom; // kg/week
    const intercept = (sumY - slope * sumX) / n; // kg at x=0
    return { slopeKgPerWeek: slope, intercept, t0Weeks };
  }, [baseSeries]);

  // Pace: use regression slope directly
  const paceKgPerWeek = regression.slopeKgPerWeek;

  // Current / baseline
  const currentWeight = baseSeries.length ? baseSeries[baseSeries.length - 1].weight : undefined;
  const startWeight = baseSeries.length ? baseSeries[0].weight : undefined;
  const totalLoss = currentWeight !== undefined && startWeight !== undefined ? startWeight - currentWeight : undefined;

  // Goal computation and projection
  const goalWeightNum = (() => {
    const n = parseFloat(goalWeightKg);
    return Number.isFinite(n) ? n : undefined;
  })();

  const trendAtTs = useCallback((ts: number) => {
    if (
      regression.slopeKgPerWeek === undefined ||
      regression.intercept === undefined ||
      regression.t0Weeks === undefined
    ) {
      return undefined;
    }
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const x = ts / weekMs - regression.t0Weeks;
    return regression.intercept + regression.slopeKgPerWeek * x;
  }, [regression.slopeKgPerWeek, regression.intercept, regression.t0Weeks]);

  const projection = useMemo(() => {
    if (!goalWeightNum) return { weeksToGoal: undefined as number | undefined, eta: undefined as Date | undefined };
    if (regression.slopeKgPerWeek === undefined || regression.slopeKgPerWeek === 0) return { weeksToGoal: undefined, eta: undefined };
    const lastTs = baseSeries.length ? baseSeries[baseSeries.length - 1].timestamp : Date.now();
    const lastTrend = trendAtTs(lastTs);
    if (lastTrend === undefined) return { weeksToGoal: undefined, eta: undefined };
    const remaining = lastTrend - goalWeightNum;
    const weeks = remaining / Math.abs(regression.slopeKgPerWeek);
    if (weeks < 0) return { weeksToGoal: 0, eta: new Date() };
    return { weeksToGoal: weeks, eta: new Date(lastTs + weeks * 7 * 24 * 60 * 60 * 1000) };
  }, [goalWeightNum, baseSeries, regression.slopeKgPerWeek, trendAtTs]);

  // Build chart data: actual weights, trend (regression), forecast (future only), goal
  const chartData = useMemo(() => {
    const data: { timestamp: number; date: string; weight?: number; trend?: number; projection?: number; goal?: number }[] = [];
    for (const p of baseSeries) {
      data.push({ timestamp: p.timestamp, date: p.date, weight: p.weight, trend: trendAtTs(p.timestamp), goal: goalWeightNum });
    }
    if (baseSeries.length) {
      const startTs = baseSeries[baseSeries.length - 1].timestamp + 7 * 24 * 60 * 60 * 1000;
      const endByWeeks = startTs + 8 * 7 * 24 * 60 * 60 * 1000;
      const endTs = goalDate ? Math.min(new Date(goalDate).getTime(), endByWeeks) : endByWeeks;
      for (let ts = startTs; ts <= endTs; ts += 7 * 24 * 60 * 60 * 1000) {
        const tr = trendAtTs(ts);
        data.push({ timestamp: ts, date: new Date(ts).toISOString(), trend: tr, goal: goalWeightNum });
      }
    }
    data.sort((a, b) => a.timestamp - b.timestamp);
    return data;
  }, [baseSeries, goalWeightNum, goalDate, trendAtTs]);

  // Shared Y domain for chart and BMI bands
  const yDomain = useMemo<[number, number]>(() => {
    if (chartData.length === 0) return [0, 1];
    const vals: number[] = [];
    for (const d of chartData) {
      if (typeof d.weight === 'number') vals.push(d.weight);
      if (typeof d.trend === 'number') vals.push(d.trend);
      if (typeof d.goal === 'number') vals.push(d.goal);
    }
    if (vals.length === 0) return [0, 1];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = Math.max(0.5, (max - min) * 0.12);
    let yMin = Math.floor(min - pad);
    let yMax = Math.ceil(max + pad);
    // If BMI bands are shown and height is known, anchor lower bound to BMI 20
    if (showBmiBands && heightCm && Number(heightCm) > 0) {
      const h = Number(heightCm) / 100;
      const w20 = 20 * h * h;
      yMin = Math.floor(w20);
      if (yMax < yMin + 5) yMax = yMin + 5; // ensure visible range
    }
    return [yMin, yMax];
  }, [chartData, showBmiBands, heightCm]);

  const yTicks = useMemo<number[]>(() => {
    const start = Math.floor(yDomain[0] / 5) * 5;
    const end = Math.ceil(yDomain[1] / 5) * 5;
    const ticks: number[] = [];
    for (let v = start; v <= end; v += 5) ticks.push(v);
    return ticks;
  }, [yDomain]);

  // Dose markers and next dose calculation
  const sortedDoses = useMemo(() => {
    return [...doses]
      .filter(d => d && d.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [doses]);

  const lastDoseDate = sortedDoses.length ? new Date(sortedDoses[sortedDoses.length - 1].date) : undefined;
  const nextDoseDate = lastDoseDate ? new Date(lastDoseDate.getTime() + 7 * 24 * 60 * 60 * 1000) : undefined;

  // Adherence metrics (simple)
  const weighInsLast7 = useMemo(() => {
    const since = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const days = new Set(sortedWeights.filter(w => new Date(w.date).getTime() >= since).map(w => new Date(w.date).toDateString()));
    return days.size;
  }, [sortedWeights]);

  const dosesLast28 = useMemo(() => {
    const since = Date.now() - 28 * 24 * 60 * 60 * 1000;
    return sortedDoses.filter(d => new Date(d.date).getTime() >= since).length;
  }, [sortedDoses]);

  const avgSymptomSeverityLast14 = useMemo(() => {
    const since = Date.now() - 14 * 24 * 60 * 60 * 1000;
    const recent = symptoms.filter(s => new Date(s.date).getTime() >= since);
    if (!recent.length) return 0;
    return Math.round((recent.reduce((s, x) => s + (x.severity || 0), 0) / recent.length) * 10) / 10;
  }, [symptoms]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-700 dark:text-gray-200">Please log in to view your dashboard.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Goal strip */}
        <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 md:p-5 mb-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="text-sm text-stone-500">🎯 Goal</div>
              <div className="text-xl md:text-2xl font-bold text-stone-900 dark:text-gray-100">
                {goalWeightNum ? `${goalWeightNum} kg` : 'Set a target weight'}
                {goalDate && <span className="text-stone-500 text-base font-medium ml-2">by {new Date(goalDate).toLocaleDateString('en-GB')}</span>}
              </div>
              <div className="text-stone-600 dark:text-stone-300 text-sm mt-1">
                {typeof paceKgPerWeek === 'number' && goalWeightNum !== undefined ? (
                  <>
                    Pace: {round1(paceKgPerWeek)} kg/wk
                    {projection.eta && (
                      <span className="ml-2">ETA: <b>{projection.eta.toLocaleDateString('en-GB')}</b></span>
                    )}
                  </>
                ) : (
                  <>Add some weights and set a goal to see projections.</>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 md:gap-3">
              <input
                type="number"
                inputMode="decimal"
                placeholder="Target weight (kg)"
                value={goalWeightKg}
                onChange={e => setGoalWeightKg(e.target.value)}
                className="border rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
              <input
                type="date"
                placeholder="Target date (optional)"
                value={goalDate}
                onChange={e => setGoalDate(e.target.value)}
                className="border rounded-lg px-3 py-2 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="text-xs text-stone-500 mt-1">Set a target weight and an optional target date to see ETA and trajectory.</div>
          </div>
        </div>

        {/* Primary chart */}
        <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 md:p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg md:text-xl font-semibold text-stone-900 dark:text-gray-100">Weight Journey</h2>
            <div className="text-sm text-stone-500">
              {startWeight !== undefined && currentWeight !== undefined && (
                <>Total change: <b>{round1(currentWeight - startWeight)} kg</b></>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mb-3 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={showDoseMarkers} onChange={e => setShowDoseMarkers(e.target.checked)} /> Show dose markers</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={showBmiBands} onChange={e => setShowBmiBands(e.target.checked)} /> Show BMI bands</label>
            <div className="flex items-center gap-2">
              <span>Height (cm)</span>
              <input
                type="number"
                className="w-24 px-2 py-1 rounded border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100"
                value={heightCm}
                onChange={e => setHeightCm(e.target.value)}
                onBlur={() => {
                  const n = parseFloat(heightCm);
                  if (Number.isFinite(n) && n >= 50 && n <= 260) {
                    localStorage.setItem('userHeightCm', String(n));
                    saveHeight(n);
                  }
                }}
                placeholder="e.g. 175"
              />
            </div>
          </div>
          {loading ? (
            <div className="text-stone-600 dark:text-stone-300">Loading...</div>
          ) : chartData.length > 0 ? (
            <div style={{ width: '100%', height: 380 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 20, right: 24, left: 48, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                  <XAxis
                    type="number"
                    dataKey="timestamp"
                    domain={[chartData[0].timestamp, chartData[chartData.length - 1].timestamp] as any}
                    tickFormatter={(t) => new Date(t).toLocaleDateString('en-GB')}
                    stroke="var(--text-secondary)"
                  />
                  <YAxis
                    label={{ value: 'Weight (kg)', angle: -90, position: 'insideLeft', offset: -20, style: { textAnchor: 'middle', fill: 'var(--text-secondary)' } }}
                    stroke="var(--text-secondary)"
                    domain={yDomain as any}
                    ticks={yTicks}
                  />
                  {showBmiBands && heightCm && Number(heightCm) > 0 && (() => {
                    const h = Number(heightCm) / 100;
                    const w20 = 20 * h * h;
                    const w25 = 25 * h * h;
                    const w30 = 30 * h * h;
                    const x1 = chartData[0].timestamp;
                    const x2 = chartData[chartData.length - 1].timestamp;
                    const yMin = yDomain[0];
                    const yMax = yDomain[1];
                    const clamp = (v: number) => Math.max(yMin, Math.min(yMax, v));
                    return (
                      <>
                        <ReferenceArea x1={x1} x2={x2} y1={clamp(w20)} y2={clamp(w25)} fill="#10b981" fillOpacity={0.16} strokeOpacity={0} />
                        <ReferenceArea x1={x1} x2={x2} y1={clamp(w25)} y2={clamp(w30)} fill="#f59e0b" fillOpacity={0.16} strokeOpacity={0} />
                        <ReferenceArea x1={x1} x2={x2} y1={clamp(w30)} y2={yMax} fill="#ef4444" fillOpacity={0.14} strokeOpacity={0} />
                      </>
                    );
                  })()}
                  <Tooltip content={({ active, payload }: any) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload;
                      return (
                        <div style={{ background: 'var(--background-light)', border: '2px solid var(--primary-color)', borderRadius: 10, padding: 12 }}>
                          <div style={{ fontWeight: 700, marginBottom: 6 }}>{formatDateLocal(d.date)}</div>
                          {typeof d.weight === 'number' && (<div style={{ color: 'var(--primary-color)' }}>Weight: <b>{round1(d.weight)} kg</b></div>)}
                          {typeof d.trend === 'number' && (<div style={{ color: 'var(--accent-color)' }}>Trend: <b>{round1(d.trend)} kg</b></div>)}
                          {goalWeightNum && <div style={{ color: 'var(--text-secondary)' }}>Goal: {goalWeightNum} kg</div>}
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Legend />
                  <Line type="monotone" dataKey="weight" name="Weight (kg)" stroke="var(--primary-color)" dot={{ r: 2 }} />
                  <Line type="monotone" dataKey="trend" name="Trend" stroke="var(--accent-color)" strokeDasharray="6 3" dot={false} />
                  {goalWeightNum && <Line type="monotone" dataKey="goal" name="Goal" stroke="#7c3aed" strokeDasharray="2 2" dot={false} />}
                  {/* Dose change markers as vertical reference lines */}
                  {showDoseMarkers && sortedDoses.map((d, i) => (
                    <ReferenceLine key={i} x={new Date(d.date).getTime()} stroke="rgba(16,185,129,0.25)" />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-stone-600 dark:text-stone-300">No weight data yet. Add some entries on the Weight page.</div>
          )}
          <div className="mt-3 text-xs text-stone-600 dark:text-stone-400 flex flex-wrap gap-4">
            <div className="flex items-center gap-2"><span style={{ width: 12, height: 2, background: 'var(--primary-color)', display: 'inline-block' }}></span> Weight</div>
            <div className="flex items-center gap-2"><span style={{ width: 12, height: 2, background: 'var(--accent-color)', display: 'inline-block' }}></span> Trend</div>
            {goalWeightNum !== undefined && <div className="flex items-center gap-2"><span style={{ width: 12, height: 2, background: '#7c3aed', display: 'inline-block' }}></span> Goal</div>}
            {showDoseMarkers && <div className="flex items-center gap-2"><span style={{ width: 2, height: 10, background: 'rgba(16,185,129,0.5)', display: 'inline-block' }}></span> Dose markers</div>}
            {showBmiBands && heightCm && Number(heightCm) > 0 && (
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: '#10b981', opacity: 0.2, display: 'inline-block' }}></span> BMI 20–25</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: '#f59e0b', opacity: 0.2, display: 'inline-block' }}></span> BMI 25–30</span>
                <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: '#ef4444', opacity: 0.2, display: 'inline-block' }}></span> BMI ≥ 30</span>
              </div>
            )}
          </div>
          {!!error && <div className="text-red-600 text-sm mt-2">{error}</div>}
        </div>

        {/* Scorecards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
            <div className="text-stone-500 text-sm">Latest weight</div>
            <div className="text-2xl font-bold text-stone-900 dark:text-gray-100 mt-1">{currentWeight !== undefined ? `${round1(currentWeight)} kg` : '—'}</div>
            {totalLoss !== undefined && (
              <div className="text-sm text-stone-600 dark:text-stone-300 mt-1">Δ from start: <b>{round1(currentWeight! - startWeight!)} kg</b></div>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
            <div className="text-stone-500 text-sm">Pace (trend)</div>
            <div className="text-2xl font-bold text-stone-900 dark:text-gray-100 mt-1">{typeof paceKgPerWeek === 'number' ? `${round1(paceKgPerWeek)} kg/wk` : '—'}</div>
            <div className="text-sm text-stone-600 dark:text-stone-300 mt-1">Negative is weight loss</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
            <div className="text-stone-500 text-sm">Weigh-in adherence (7d)</div>
            <div className="text-2xl font-bold text-stone-900 dark:text-gray-100 mt-1">{weighInsLast7}/7</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
            <div className="text-stone-500 text-sm">Doses last 28d</div>
            <div className="text-2xl font-bold text-stone-900 dark:text-gray-100 mt-1">{dosesLast28}</div>
            <div className="text-sm text-stone-600 dark:text-stone-300 mt-1">Expected ~4</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
            <div className="text-stone-500 text-sm">Symptoms (avg 14d)</div>
            <div className="text-2xl font-bold text-stone-900 dark:text-gray-100 mt-1">{avgSymptomSeverityLast14}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
            <div className="text-stone-500 text-sm">Next dose</div>
            <div className="text-2xl font-bold text-stone-900 dark:text-gray-100 mt-1">{nextDoseDate ? nextDoseDate.toLocaleDateString('en-GB') : '—'}</div>
          </div>
        </div>

        {/* Next actions */}
        <div className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-2xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-stone-900 dark:text-gray-100 mb-3">Next best actions</h3>
          <div className="flex flex-wrap gap-2">
            <a href="/weight" className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg">Log today’s weight</a>
            <a href="/log-dose" className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg">Log dose</a>
            <a href="/symptoms" className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg">Log symptoms</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyDashboardPage;


